import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import PlaylistAddRoundedIcon from "@mui/icons-material/PlaylistAddRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import {
  Avatar, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, IconButton, InputAdornment, Menu, MenuItem, Paper, Stack,
  Switch, TextField, Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { SoftDataGrid } from "../../../components/data/SoftDataGrid";
import { PermissionGate } from "../../../components/auth/PermissionGate";
import {
  createBrand, createBrandsBulk, deleteBrand, getBrands, updateBrand,
} from "../../../services/brandsService";
import { getApiErrorMessage } from "../../../utils/apiData";

const fieldSx = {
  "& .MuiInputBase-root": { borderRadius: 0, fontSize: "0.72rem" },
  "& .MuiInputLabel-root": { fontSize: "0.72rem" },
};

const extractPaginator = (payload) => payload?.brands ?? payload?.data?.brands ?? payload?.data ?? {};
const extractRows = (payload) => {
  const paginator = extractPaginator(payload);
  if (Array.isArray(paginator?.data)) return paginator.data;
  return Array.isArray(paginator) ? paginator : [];
};

function SquareDialog({ open, onClose, title, children, actions, maxWidth = "md" }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth={maxWidth} fullWidth PaperProps={{ sx: { borderRadius: 0 } }}>
      <DialogTitle sx={{ fontSize: "0.9rem", fontWeight: 850 }}>{title}</DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "#FBFCFD" }}>{children}</DialogContent>
      {actions ? <DialogActions>{actions}</DialogActions> : null}
    </Dialog>
  );
}

function BrandForm({ brand, busy, onCancel, onSubmit }) {
  const { control, handleSubmit } = useForm({
    defaultValues: {
      name: brand?.name || "",
      code: brand?.code || "",
      description: brand?.description || "",
      is_active: brand?.is_active ?? true,
    },
  });

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={1.2}>
        <Controller name="name" control={control} rules={{ required: true }} render={({ field }) => <TextField {...field} label="Brand name" sx={fieldSx} />} />
        <Controller name="code" control={control} render={({ field }) => <TextField {...field} label="Brand code" sx={fieldSx} />} />
        <Controller name="description" control={control} render={({ field }) => <TextField {...field} label="Description" multiline minRows={3} sx={fieldSx} />} />
        <Controller name="is_active" control={control} render={({ field }) => (
          <FormControlLabel control={<Switch checked={Boolean(field.value)} onChange={(_, checked) => field.onChange(checked)} />} label="Active brand" />
        )} />
      </Stack>
      <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 2 }}>
        <Button onClick={onCancel} color="inherit" sx={{ borderRadius: 0 }}>Cancel</Button>
        <Button type="submit" variant="contained" disabled={busy} sx={{ borderRadius: 0 }}>{busy ? "Saving..." : "Save"}</Button>
      </Stack>
    </Box>
  );
}

function BulkBrandForm({ busy, onCancel, onSubmit }) {
  const { control, handleSubmit } = useForm({ defaultValues: { brands: [{ name: "", code: "", description: "", is_active: true }] } });
  const { fields, append, remove } = useFieldArray({ control, name: "brands" });

  return (
    <Box component="form" onSubmit={handleSubmit((values) => onSubmit(values.brands))}>
      <Stack spacing={1}>
        {fields.map((item, index) => (
          <Paper key={item.id} variant="outlined" sx={{ p: 1.2, borderRadius: 0 }}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: "0.7rem", fontWeight: 850 }}>Brand {index + 1}</Typography>
              {fields.length > 1 ? <Button color="error" onClick={() => remove(index)}>Remove</Button> : null}
            </Stack>
            <Stack spacing={1}>
              <Controller name={`brands.${index}.name`} control={control} rules={{ required: true }} render={({ field }) => <TextField {...field} label="Brand name" sx={fieldSx} />} />
              <Controller name={`brands.${index}.code`} control={control} render={({ field }) => <TextField {...field} label="Brand code" sx={fieldSx} />} />
              <Controller name={`brands.${index}.description`} control={control} render={({ field }) => <TextField {...field} label="Description" multiline minRows={2} sx={fieldSx} />} />
            </Stack>
          </Paper>
        ))}
      </Stack>
      <Button startIcon={<PlaylistAddRoundedIcon />} onClick={() => append({ name: "", code: "", description: "", is_active: true })} sx={{ mt: 1, borderRadius: 0 }}>Add another</Button>
      <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 2 }}>
        <Button onClick={onCancel} color="inherit" sx={{ borderRadius: 0 }}>Cancel</Button>
        <Button type="submit" variant="contained" disabled={busy} sx={{ borderRadius: 0 }}>{busy ? "Saving..." : "Create brands"}</Button>
      </Stack>
    </Box>
  );
}

function RowMenu({ row, onEdit, onDelete }) {
  const [anchor, setAnchor] = useState(null);
  return (
    <>
      <IconButton size="small" onClick={(event) => setAnchor(event.currentTarget)}><MoreVertRoundedIcon /></IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)} PaperProps={{ sx: { borderRadius: 0 } }}>
        <PermissionGate permission="update_brands"><MenuItem onClick={() => { setAnchor(null); onEdit(row); }}><EditOutlinedIcon sx={{ mr: 1, fontSize: 16 }} />Edit</MenuItem></PermissionGate>
        <PermissionGate permission="delete_brands"><MenuItem onClick={() => { setAnchor(null); onDelete(row); }} sx={{ color: "error.main" }}><DeleteOutlineRoundedIcon sx={{ mr: 1, fontSize: 16 }} />Delete</MenuItem></PermissionGate>
      </Menu>
    </>
  );
}

export function BrandsPage() {
  const client = useQueryClient();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [dialog, setDialog] = useState({ type: null, brand: null });
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPaginationModel((current) => ({ ...current, page: 0 }));
    }, 450);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const query = useQuery({
    queryKey: ["brands", search, paginationModel.page, paginationModel.pageSize],
    queryFn: () => getBrands({ search, page: paginationModel.page + 1, per_page: paginationModel.pageSize }),
  });

  const rows = extractRows(query.data);
  const paginator = extractPaginator(query.data);

  const save = useMutation({
    mutationFn: ({ brand, values }) => brand ? updateBrand(brand.id, values) : createBrand(values),
    onSuccess: (response) => {
      client.invalidateQueries({ queryKey: ["brands"] });
      client.invalidateQueries({ queryKey: ["brand-options"] });
      setDialog({ type: null, brand: null });
      setMessage(response?.message || "Brand saved successfully.");
    },
    onError: (error) => setMessage(getApiErrorMessage(error)),
  });

  const bulk = useMutation({
    mutationFn: createBrandsBulk,
    onSuccess: (response) => {
      client.invalidateQueries({ queryKey: ["brands"] });
      client.invalidateQueries({ queryKey: ["brand-options"] });
      setDialog({ type: null, brand: null });
      setMessage(response?.message || "Brands created successfully.");
    },
    onError: (error) => setMessage(getApiErrorMessage(error)),
  });

  const remove = useMutation({
    mutationFn: deleteBrand,
    onSuccess: (response) => {
      client.invalidateQueries({ queryKey: ["brands"] });
      client.invalidateQueries({ queryKey: ["brand-options"] });
      setDialog({ type: null, brand: null });
      setMessage(response?.message || "Brand deleted successfully.");
    },
    onError: (error) => setMessage(getApiErrorMessage(error)),
  });

  const columns = [
    { field: "avatar", headerName: "", width: 52, renderCell: (params) => <Avatar sx={{ width: 28, height: 28 }}>{params.row.name?.slice(0, 2).toUpperCase()}</Avatar> },
    { field: "name", headerName: "Brand name", minWidth: 200, flex: 1 },
    { field: "code", headerName: "Code", minWidth: 120, flex: 0.5, valueGetter: (_, row) => row.code || "—" },
    { field: "description", headerName: "Description", minWidth: 250, flex: 1.2, valueGetter: (_, row) => row.description || "—" },
    { field: "shops_count", headerName: "Shops", width: 90 },
    { field: "status", headerName: "Status", width: 90, renderCell: (params) => <Chip label={params.row.is_active ? "Active" : "Inactive"} size="small" sx={{ borderRadius: 0 }} /> },
    { field: "actions", headerName: "", width: 54, renderCell: (params) => <RowMenu row={params.row} onEdit={(brand) => setDialog({ type: "edit", brand })} onDelete={(brand) => setDialog({ type: "delete", brand })} /> },
  ];

  return (
    <Box sx={{ p: { xs: 1.2, md: 2 } }}>
      <Paper variant="outlined" sx={{ borderRadius: 0 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ p: 1.2, borderBottom: "1px solid #E3E7EC" }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 850 }}>Brands</Typography>
            <Typography sx={{ color: "#667085", fontSize: "0.66rem" }}>Manage brands used by shops.</Typography>
          </Box>
          <TextField value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search brands" size="small" InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon /></InputAdornment> }} sx={{ "& .MuiInputBase-root": { borderRadius: 0 } }} />
          <PermissionGate permission="create_brands"><Button variant="outlined" startIcon={<PlaylistAddRoundedIcon />} onClick={() => setDialog({ type: "bulk", brand: null })} sx={{ borderRadius: 0 }}>Add many</Button></PermissionGate>
          <PermissionGate permission="create_brands"><Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setDialog({ type: "create", brand: null })} sx={{ borderRadius: 0 }}>New brand</Button></PermissionGate>
        </Stack>
        <SoftDataGrid rows={rows} columns={columns} loading={query.isLoading} rowCount={paginator?.total ?? rows.length} paginationMode="server" paginationModel={paginationModel} onPaginationModelChange={setPaginationModel} pageSizeOptions={[10, 20, 50, 100]} getRowId={(row) => row.id} />
      </Paper>

      <SquareDialog open={dialog.type === "create" || dialog.type === "edit"} onClose={() => setDialog({ type: null, brand: null })} title={dialog.type === "edit" ? "Edit brand" : "Create brand"}>
        <BrandForm key={`${dialog.type}-${dialog.brand?.id || "new"}`} brand={dialog.type === "edit" ? dialog.brand : null} busy={save.isPending} onCancel={() => setDialog({ type: null, brand: null })} onSubmit={(values) => save.mutate({ brand: dialog.type === "edit" ? dialog.brand : null, values })} />
      </SquareDialog>

      <SquareDialog open={dialog.type === "bulk"} onClose={() => setDialog({ type: null, brand: null })} title="Create many brands">
        <BulkBrandForm busy={bulk.isPending} onCancel={() => setDialog({ type: null, brand: null })} onSubmit={(brands) => bulk.mutate(brands)} />
      </SquareDialog>

      <SquareDialog open={dialog.type === "delete"} onClose={() => setDialog({ type: null, brand: null })} title="Delete brand" maxWidth="sm" actions={<><Button onClick={() => setDialog({ type: null, brand: null })} color="inherit">Cancel</Button><Button color="error" variant="contained" onClick={() => remove.mutate(dialog.brand.id)}>Delete</Button></>}>
        <Typography>Delete <strong>{dialog.brand?.name}</strong>?</Typography>
      </SquareDialog>

      <Dialog open={Boolean(message)} onClose={() => setMessage(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 0 } }}>
        <DialogTitle sx={{ fontWeight: 850 }}>Brands</DialogTitle>
        <DialogContent dividers><Typography sx={{ fontSize: "0.72rem" }}>{message}</Typography></DialogContent>
        <DialogActions><Button onClick={() => setMessage(null)} variant="contained">OK</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
